import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleAlert, RefreshCw } from 'lucide-react';
import { api, type CapacityView, type QueueEntry, type BoardEntry, type LockView, type Pair } from '../api.js';
import { Screen, Section, Btn, Badge, EmptyState } from '../ui/kit.js';
import { lifeMeta } from '../phases.js';

/* Capacity (V8 spec §8, §9) — the one screen that answers "can anything run
 * right now, and where".
 *
 * Three questions, three bands, no cards padded with nothing. What the box
 * can carry and what is in each slot; which (account, model) pairs have
 * headroom, measured rather than guessed; and what is coordinated between
 * jobs right now — the merge queue, the locks, and the board every session
 * is shown. The numbers are the point, so they are the biggest thing on the
 * screen, and every one of them says when it was true.
 */

const gib = (b: number) => `${(b / 1024 ** 3).toFixed(1)} GiB`;

/* A board is something you take in at a glance. Thirty-four cards is a page. */
const BOARD_SHOWN = 8;

function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const s = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function until(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return 'now';
  const m = Math.round(ms / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  return `in ${h}h ${m % 60}m`;
}

export function Capacity() {
  const nav = useNavigate();
  const [cap, setCap] = useState<CapacityView | null>(null);
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [board, setBoard] = useState<BoardEntry[]>([]);
  const [locks, setLocks] = useState<LockView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [readAt, setReadAt] = useState<string | null>(null);
  const [boardOpen, setBoardOpen] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setBusy(true);
    try {
      const [c, q, b, l] = await Promise.all([
        api.get<CapacityView>(`/api/capacity${refresh ? '?refresh=1' : ''}`),
        api.get<{ queue: QueueEntry[] }>('/api/merge-queue'),
        api.get<{ board: BoardEntry[] }>('/api/board'),
        api.get<{ locks: LockView[] }>('/api/locks'),
      ]);
      setCap(c);
      setQueue(q.queue ?? []);
      setBoard(b.board ?? []);
      setLocks(l.locks ?? []);
      setReadAt(new Date().toISOString());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 20_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <Screen
      className="ops-screen--wide"
      eyebrow="The box"
      title="Capacity"
      sub={readAt ? <>Measured on the box · read {ago(readAt)}</> : 'Reading the box…'}
      actions={
        <Btn kind="quiet" onClick={() => void load(true)} disabled={busy}>
          <RefreshCw size={15} /> {busy ? 'Measuring…' : 'Measure now'}
        </Btn>
      }
    >
      {error ? (
        <div className="ops-cap__error" role="alert">
          <CircleAlert size={16} /> {error}
        </div>
      ) : null}

      {cap ? <Slots cap={cap} onOpen={(id) => nav(`/jobs/${id}`)} /> : null}
      {cap ? <Matrix cap={cap} /> : null}

      <Section title="Merge queue" aside={queue.length ? `${queue.length} waiting` : undefined}>
        {queue.length ? (
          <div className="ops-cap__queue">
            {queue.map((q) => (
              <button type="button" key={q.jobId} className="ops-cap__qrow" onClick={() => nav(`/jobs/${q.jobId}`)}>
                <span className="ops-cap__qpos">{q.position}</span>
                <span className="ops-cap__qmain">
                  <span className="ops-cap__qtitle">{q.title}</span>
                  <span className="ops-cap__qsub">
                    {q.prNumber ? `#${q.prNumber} · ` : ''}
                    {q.detail ?? (q.state === 'waiting' ? 'waiting its turn' : q.state)} · joined {ago(q.enteredAt)}
                  </span>
                </span>
                <Badge tone={q.state === 'merging' ? 'run' : q.state === 'updating' ? 'run' : 'wait'}>{q.state}</Badge>
              </button>
            ))}
          </div>
        ) : (
          <EmptyState title="Nothing waiting to merge">Merges are serialised: one job updates onto main, goes green and lands before the next one starts.</EmptyState>
        )}
      </Section>

      <Section title="Locks" aside={locks.length ? `${locks.length} held` : undefined}>
        {locks.length ? (
          <div className="ops-cap__locks">
            {locks.map((l) => (
              <button type="button" key={l.name} className="ops-cap__lock" onClick={() => nav(`/jobs/${l.jobId}`)}>
                <span className="ops-cap__lockname">{l.name}</span>
                <span className="ops-cap__lockby">
                  {l.title} · taken {ago(l.takenAt)}
                </span>
                {l.note ? <span className="ops-cap__locknote">{l.note}</span> : null}
              </button>
            ))}
          </div>
        ) : (
          <EmptyState title="No lock is held">Only one job at a time may add a Prisma migration. A job that needs the lock and cannot get it is told to do the rest of its work first.</EmptyState>
        )}
      </Section>

      <Section title="The job board" aside={board.length ? `${board.length} in flight` : undefined}>
        {board.length ? (
          <div className="ops-cap__board">
            {(boardOpen ? board : board.slice(0, BOARD_SHOWN)).map((b) => {
              const m = lifeMeta(b.lifecycle);
              return (
                <button type="button" key={b.jobId} className="ops-cap__bcard" onClick={() => nav(`/jobs/${b.jobId}`)}>
                  <span className="ops-cap__bhead">
                    <Badge tone={m.tone}>{m.label}</Badge>
                    {b.branch ? <code className="ops-cap__bbranch">{b.branch}</code> : null}
                  </span>
                  <span className="ops-cap__btitle">{b.title}</span>
                  {b.paths.length ? (
                    <span className="ops-cap__bpaths">
                      {[...new Set(b.paths.map((p) => p.split('/').slice(0, 3).join('/')))].slice(0, 6).map((p) => (
                        <code key={p}>{p}</code>
                      ))}
                    </span>
                  ) : (
                    <span className="ops-cap__bnone">nothing committed yet</span>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <EmptyState title="No other job is running">Every running job's branch and the paths it has touched are shown here, and injected into every session's context so one job never surprises another.</EmptyState>
        )}
        {board.length > BOARD_SHOWN ? (
          <button type="button" className="ops-cap__more" onClick={() => setBoardOpen((v) => !v)}>
            {boardOpen ? 'Show fewer' : `Show the other ${board.length - BOARD_SHOWN}`}
          </button>
        ) : null}
      </Section>
    </Screen>
  );
}

function Slots(props: { cap: CapacityView; onOpen: (id: string) => void }) {
  const { cap } = props;
  return (
    <Section
      title="Slots"
      aside={`${cap.slots.filter((s) => s.jobId).length} of ${cap.configuredSlots} in use`}
    >
      <div className="ops-cap__meter">
        <div className="ops-cap__figures">
          <Figure big value={String(cap.configuredSlots)} label="slots configured" />
          <Figure big value={String(cap.fullLoadSlots)} label="the box can carry" tone={cap.oversubscribed ? 'bad' : 'ok'} />
          <Figure value={gib(cap.measuredPeakBytes)} label="peak per job, measured" />
          <Figure value={gib(cap.totalBytes)} label="box memory" />
          <Figure value={gib(cap.boxSizeForFour)} label="a box for four jobs" />
        </div>
        <p className={`ops-cap__note${cap.oversubscribed ? ' ops-cap__note--bad' : ''}`}>{cap.note}</p>
        <p className="ops-cap__from">Peak measured from {cap.measuredFrom}.</p>
      </div>
      <div className="ops-cap__slots">
        {cap.slots.map((s) => (
          <div key={s.index} className={`ops-cap__slot${s.jobId ? ' ops-cap__slot--busy' : ''}`}>
            <div className="ops-cap__slothead">
              <span className="ops-cap__slotn">Slot {s.index}</span>
              <Badge tone={s.jobId ? 'run' : 'wait'}>{s.jobId ? 'in use' : 'free'}</Badge>
            </div>
            {s.jobId ? (
              <button type="button" className="ops-cap__slotjob" onClick={() => props.onOpen(s.jobId!)}>
                {s.title ?? s.jobId}
              </button>
            ) : (
              <p className="ops-cap__slotfree">Ready for the next job.</p>
            )}
            {s.ports ? (
              <dl className="ops-cap__ports">
                {Object.entries(s.ports).slice(0, 6).map(([k, v]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>
        ))}
      </div>
    </Section>
  );
}

function Matrix(props: { cap: CapacityView }) {
  const { cap } = props;
  const at = (accountId: string, model: string): Pair | undefined =>
    cap.matrix.find((p) => p.accountId === accountId && p.model === model);
  const free = cap.matrix.filter((p) => p.free && p.enabled && !(p.utilization != null && p.utilization >= 0.97)).length;
  return (
    <Section
      title="Accounts and models"
      aside={free ? `${free} of ${cap.matrix.length} pairs with room` : 'no pair has much room left'}
    >
      <p className="ops-cap__explain">
        The resource is the <strong>pair</strong>, not the account: one account can be out of Fable and still have Opus.
        Headroom is read from the API's own rate-limit headers on the pair's own model, not guessed from a failure.
      </p>
      <div className="ops-cap__matrix" role="table">
        <div className="ops-cap__mrow ops-cap__mrow--head" role="row">
          <span role="columnheader">Account</span>
          {cap.models.map((m) => (
            <span key={m.id} role="columnheader">
              {m.label}
            </span>
          ))}
        </div>
        {cap.accounts.map((a) => (
          <div className="ops-cap__mrow" role="row" key={a.id}>
            <span className="ops-cap__macct" role="cell">
              <span className="ops-cap__mlabel">{a.label}</span>
              <code>{a.id}</code>
            </span>
            {cap.models.map((m) => {
              const p = at(a.id, m.id);
              const pct = p?.utilization != null ? Math.round(p.utilization * 100) : null;
              const out = !p?.free;
              /* A pair the API has not refused yet but which is at 99% is
                 not "free" in any sense a reader means. Selection already
                 demotes it below a pair with real room; the word has to say
                 the same thing, or the screen contradicts the behaviour. */
              const nearly = !out && pct !== null && pct >= 97;
              return (
                <span className={`ops-cap__cell${out ? ' ops-cap__cell--out' : nearly ? ' ops-cap__cell--nearly' : ''}`} role="cell" key={m.id}>
                  <span className="ops-cap__cellstate">{!p?.enabled ? 'off' : out ? 'out' : nearly ? 'nearly out' : 'free'}</span>
                  {pct != null ? (
                    <span className="ops-cap__bar" aria-label={`${pct}% used`}>
                      <span style={{ width: `${Math.min(100, pct)}%` }} />
                    </span>
                  ) : null}
                  <span className="ops-cap__cellsub">
                    {pct != null ? `${pct}% used` : 'not measured'}
                    {out && p?.limitedUntil ? ` · back ${until(p.limitedUntil)}` : ''}
                  </span>
                  <span className="ops-cap__cellage">{p?.sampledAt ? ago(p.sampledAt) : '—'}</span>
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </Section>
  );
}

function Figure(props: { value: string; label: string; big?: boolean; tone?: 'ok' | 'bad' }) {
  return (
    <div className={`ops-cap__fig${props.big ? ' ops-cap__fig--big' : ''}${props.tone ? ` ops-cap__fig--${props.tone}` : ''}`}>
      <span className="ops-cap__figv">{props.value}</span>
      <span className="ops-cap__figl">{props.label}</span>
    </div>
  );
}
